import { Builder } from '@builder.io/react'
import { Cache } from 'aws-amplify'
import { navigate } from 'gatsby'
import React, { useEffect, useState } from 'react'

import CdnImage from 'components/util/CdnImage'
import { growthbookBuilderVariantUtil } from 'components/util/Growthbook'
import { getCognitoId } from 'components/util/UserId'
import { HeadingClass, HeadingType } from 'enums'
import farmToBowlArrow from '../../../images/farm-to-bowl-desktop-arrow.png'
import CustomHeading, { headingTypePropObj, headingClassPropObj } from '../common/heading'

interface Props {
  backgroundColor: string
  cloudinaryImageOne: any
  cloudinaryImageTwo: any
  cloudinaryImageThree: any
  builderImageOne: any
  builderImageTwo: any
  builderImageThree: any
  imageOneAltText: string
  imageTwoAltText: string
  imageThreeAltText: string
  headingText: string
  headingTextColor: string
  headingType: HeadingType
  headingClass: HeadingClass
  headingContentTextOne: string
  headingContentTextTwo: string
  headingContentTextThree: string
  contentTextOne: string
  contentTextTwo: string
  contentTextThree: string
  footerText: string
  showButton: boolean
  footerBackground: string
  coupon: string
  growthbookFeatureId: string
  enabled: boolean
  showArrow: boolean
}

const ThreeImageText = ({
  backgroundColor,
  cloudinaryImageOne,
  cloudinaryImageTwo,
  cloudinaryImageThree,
  builderImageOne,
  builderImageTwo,
  builderImageThree,
  imageOneAltText = '',
  imageTwoAltText = '',
  imageThreeAltText = '',
  headingText,
  headingTextColor,
  headingType,
  headingClass,
  headingContentTextOne,
  headingContentTextTwo,
  headingContentTextThree,
  contentTextOne,
  contentTextTwo,
  contentTextThree,
  footerText,
  showButton,
  footerBackground,
  coupon = '',
  growthbookFeatureId = '',
  enabled = false,
  showArrow = true
}: Props) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [gbVariant, setGBVariant] = useState<number>(1)
  const dogDetailsPage = 'dog-details'

  useEffect(() => {
    // GB for button text 1:Create Your Plan 2:Create My Plan
    const weights = [50, 50]
    const getGBVariant = growthbookBuilderVariantUtil(growthbookFeatureId, weights, enabled, 1)
    setGBVariant(getGBVariant)
    // Activation metric
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'three_image_text_cta_load', {
        page: 'home_page'
      })
    }
  }, [])

  useEffect(() => {
    const authenticateUser = async () => {
      try {
        const { username } = await getCognitoId()

        if (username) {
          setIsLoggedIn(true)
        }
      } catch (e) {
        console.log(e)
      }
    }

    authenticateUser()
  }, [isLoggedIn])

  const getButtonText = () => {
    if (enabled) {
      return `${isLoggedIn ? 'Update' : 'Create'} ${gbVariant === 2 ? 'My' : 'Your'} Plan`
    } else {
      return `${isLoggedIn ? 'Update' : 'Create'} Your Plan`
    }
  }

  const getHeading = () => {
    return (
      <div className="mt-8 flex items-center justify-center">
        <div className="relative inline-flex w-fit">
          <CustomHeading
            classList="line-height-fix w-4/5 mx-auto lg:w-full h-full tracking-tight text-gray-900 text-left font-medium text-center mt-4 lg:text-left"
            headingClass={headingClass}
            headingType={headingType}
            color={headingTextColor}
          >
            {headingText}
          </CustomHeading>
          {showArrow && (
            <>
              <img
                src={farmToBowlArrow}
                alt={'Arrow'}
                className="absolute z-40 hidden lg:block"
                style={{ top: '5%', left: '75%', width: '8rem' }}
              />
              <img
                src={farmToBowlArrow}
                alt={'Arrow'}
                className="absolute z-40 block lg:hidden"
                style={{ top: '70%', left: '50%', width: '4rem' }}
              />
            </>
          )}
        </div>
      </div>
    )
  }
  const getContent = () => {
    return (
      <div className="mx-auto flex flex-col items-center justify-center lg:mt-10 lg:h-[30rem] lg:flex-row">
        <div className="flex w-full items-center pt-4 px-4 text-left lg:ml-0 lg:mt-24 lg:flex-col lg:justify-center lg:pl-2 lg:text-center">
          <CdnImage
            className="mt-6 block h-28 rounded-2xl object-cover lg:-mt-16 lg:mt-0 lg:h-[15rem] lg:w-[20rem]"
            url={builderImageOne}
            alt={imageOneAltText}
            steps={[168, 250, 320]}
          />

          {/* {builderImageOne ? (
            <CdnImage
              className="mt-6 block h-28 rounded-2xl object-cover lg:-mt-16 lg:h-[15rem] lg:w-[20rem]"
              url={builderImageOne}
              alt={imageOneAltText}
              steps={[168, 250, 320]}
            />
          ) : (
            <CloudinaryImage
              className="mt-6 block h-28 rounded-2xl object-cover lg:-mt-16 lg:h-[15rem] lg:w-[20rem]"
              publicId={cloudinaryImageOne?.public_id}
              alt={imageOneAltText}
              steps={[168, 250, 320]}
            />
          )} */}
          <div className="w-11/12 lg:h-[14rem]">
            <p
              className={`enable-list-styles mt-4 w-full pl-4 text-left text-xl lg:ml-0 lg:text-center font-sans font-medium`}
              dangerouslySetInnerHTML={{ __html: headingContentTextOne }}
            ></p>
            <p
              className={`enable-list-styles mt-4 h-full w-full pl-4 text-left text-xs lg:ml-0 lg:text-center lg:text-base`}
              dangerouslySetInnerHTML={{ __html: contentTextOne }}
            ></p>
          </div>
        </div>
        <div className="flex w-full flex-row-reverse items-center pt-4 px-4 text-left lg:ml-0 lg:mt-24 lg:flex-col lg:justify-center lg:pl-2 lg:text-center">
          <CdnImage
            className="mt-6 block h-28 rounded-2xl object-cover lg:-mt-16 lg:mt-0 lg:h-[15rem] lg:w-[20rem]"
            url={builderImageTwo}
            alt={imageTwoAltText}
            steps={[168, 250, 320]}
          />

          {/* {builderImageTwo ? (
            <CdnImage
              className="mt-6 block h-28 rounded-2xl object-cover lg:-mt-16 lg:h-[15rem] lg:w-[20rem]"
              url={builderImageTwo}
              alt={imageTwoAltText}
              steps={[168, 250, 320]}
            />
          ) : (
            <CloudinaryImage
              className="mt-6 block h-28 rounded-2xl object-cover lg:-mt-16 lg:h-[15rem] lg:w-[20rem]"
              publicId={cloudinaryImageTwo?.public_id}
              alt={imageTwoAltText}
              steps={[168, 250, 320]}
            />
          )} */}

          <div className="w-11/12 lg:h-[14rem]">
            <p
              className={`enable-list-styles mt-4 w-full text-left text-xl lg:ml-0 lg:text-center font-sans font-medium`}
              dangerouslySetInnerHTML={{ __html: headingContentTextTwo }}
            ></p>
            <p
              className={`enable-list-styles h-full w-full pt-4 text-left text-xs lg:ml-0 lg:text-center lg:text-base`}
              dangerouslySetInnerHTML={{ __html: contentTextTwo }}
            ></p>
          </div>
        </div>
        <div className="flex w-full items-center pt-4 px-4 text-left lg:ml-0 lg:mt-24 lg:flex-col lg:justify-center lg:pl-2 lg:text-center">
          <CdnImage
            className="mt-6 block h-28 rounded-2xl object-cover lg:-mt-16 lg:mt-0 lg:h-[15rem] lg:w-[20rem]"
            url={builderImageThree}
            alt={imageThreeAltText}
            steps={[168, 250, 320]}
          />

          {/* {builderImageThree ? (
            <CdnImage
              className="mt-6 block h-28 rounded-2xl object-cover lg:-mt-16 lg:h-[15rem] lg:w-[20rem]"
              url={builderImageThree}
              alt={imageThreeAltText}
              steps={[168, 250, 320]}
            />
          ) : (
            <CloudinaryImage
              className="mt-6 block h-28 rounded-2xl object-cover lg:-mt-16 lg:h-[15rem] lg:w-[20rem]"
              publicId={cloudinaryImageThree?.public_id}
              alt={imageThreeAltText}
              steps={[168, 250, 320]}
            />
          )}
 */}
          <div className="w-11/12 lg:h-[14rem]">
            <p
              className={`enable-list-styles mt-4 w-full pl-4 text-left text-xl lg:ml-0 lg:text-center font-sans font-medium`}
              dangerouslySetInnerHTML={{ __html: headingContentTextThree }}
            ></p>
            <p
              className={`enable-list-styles mt-4 h-full w-full pl-4 text-left text-xs lg:ml-0 lg:text-center lg:text-base`}
              dangerouslySetInnerHTML={{ __html: contentTextThree }}
            ></p>
          </div>
        </div>
      </div>
    )
  }
  const getFooter = () => {
    return (
      <div className="flex flex-col items-center justify-center lg:mt-4">
        <div className="relative inline-flex w-fit">
          <CustomHeading
            classList="line-height-fix w-full h-full tracking-tight text-gray-900 text-left font-medium text-center mt-8 lg:text-left"
            headingClass={headingClass}
            headingType={headingType}
            color={headingTextColor}
          >
            {footerText}
          </CustomHeading>
        </div>
        {showButton && (
          <button
            type="button"
            className="mx-1 mt-4 flex w-full justify-center rounded-3xl border border-transparent bg-primary px-4 py-2 text-base font-medium hover:underline md:w-[90%] md:text-lg lg:mt-4"
            onClick={() => {
              if (isLoggedIn) {
                navigate('/account/')
              } else {
                if (typeof window.gtag !== 'undefined') {
                  window.gtag('event', 'three_image_text_cta_submit', {
                    page: 'home_page'
                  })
                }
                window.location.href = `/signup/${dogDetailsPage}/${coupon ? `?coupon=${coupon}` : ''}`
              }
            }}
            style={{ color: '#fff' }}
          >
            {getButtonText()}
          </button>
        )}
      </div>
    )
  }

  return (
    <>
      <div
        className="relative mx-auto flex w-full flex-col items-center justify-center lg:rounded-t-[30px] bg-white bg-auto bg-no-repeat lg:w-4/5 lg:bg-100%"
        style={{ backgroundColor: `${backgroundColor}` }}
      >
        {getHeading()}
        {getContent()}
        {getFooter()}
      </div>
      <div
        className="font-sans-serif relative mx-auto mb-4 block h-16 w-full bg-cover bg-bottom bg-no-repeat lg:h-20 lg:w-4/5"
        style={{
          backgroundImage: footerBackground ? `url(${footerBackground})` : 'none'
        }}
      />
    </>
  )
}

export default ThreeImageText

Builder.registerComponent(ThreeImageText, {
  name: 'ThreeImageText',
  inputs: [
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'white'
    },
    // {
    //   name: 'cloudinaryImageOne',
    //   type: 'cloudinaryImageEditor'
    // },
    // {
    //   name: 'cloudinaryImageTwo',
    //   type: 'cloudinaryImageEditor'
    // },
    // {
    //   name: 'cloudinaryImageThree',
    //   type: 'cloudinaryImageEditor'
    // },
    {
      name: 'builderImageOne',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'builderImageTwo',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'builderImageThree',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'imageOneAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'imageTwoAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'imageThreeAltText',
      type: 'text',
      defaultValue: ''
    },
    {
      ...headingTypePropObj
    },
    {
      ...headingClassPropObj
    },
    {
      name: 'headingText',
      type: 'text',
      defaultValue: 'Javascript for Beginners'
    },
    {
      name: 'headingTextColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'headingContentTextOne',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'headingContentTextTwo',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'headingContentTextThree',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'contentTextOne',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'contentTextTwo',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'contentTextThree',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'footerText',
      type: 'text',
      defaultValue: 'Javascript for Beginners'
    },
    {
      name: 'showButton',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'footerBackground',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'coupon',
      type: 'text',
      defaultValue: ''
    },
    {
      name: 'growthbookFeatureId',
      friendlyName: 'Growthbook Feature ID',
      type: 'string',
      defaultValue: ''
    },
    {
      name: 'enabled',
      friendlyName: 'Enabled',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'showArrow',
      type: 'boolean',
      defaultValue: true
    }
  ]
})
